import { apiClient } from '@/api/core';
import { API_CONFIG } from '@/api/core/apiConfig';
import { buildQueryString } from '@/api/shared/queryBuilder';
import type { ApiResponse } from '@/api/shared';
import type {
  LC_CreateWorkingDayIn,
  LC_CreateWorkingDayOut,
  LC_UpdateWorkingDayIn,
  LC_UpdateWorkingDayOut,
  LC_ToggleWorkingDayStatusIn,
  LC_ToggleWorkingDayStatusOut,
  LC_GetWorkingDaysListOut,
  WorkingDayListParams,
  WorkingDayListResult,
} from './workingDay.types';
import { mapWorkingDayListFromTransport } from './workingDay.mapper';


export class WorkingDayService {
  async list(
    params?: WorkingDayListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<WorkingDayListResult>> {
    const query = buildQueryString({
      page: params?.page,
      pageSize: params?.pageSize,
      searchText: params?.searchText,
      orderBy: params?.orderBy,
      orderDirection: params?.orderDirection,
    });

    const response = await apiClient.get<LC_GetWorkingDaysListOut>(
      `${API_CONFIG.ENDPOINTS.WORKING_DAYS.LIST}${query}`,
      { signal },
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: mapWorkingDayListFromTransport(response.data),
      };
    }

    return { success: false, data: null, error: response.error };
  }

  async create(
    data: LC_CreateWorkingDayIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_CreateWorkingDayOut>> {
    const response = await apiClient.post<LC_CreateWorkingDayOut>(
      API_CONFIG.ENDPOINTS.WORKING_DAYS.CREATE,
      data,
      { signal },
    );

    if (response.success) {
      return { ...response, data: response.data ?? { message: null } };
    }

    return { ...response, data: null };
  }

  async update(
    id: number,
    data: LC_UpdateWorkingDayIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_UpdateWorkingDayOut>> {
    const response = await apiClient.put<LC_UpdateWorkingDayOut>(
      API_CONFIG.ENDPOINTS.WORKING_DAYS.UPDATE(id),
      data,
      { signal },
    );

    if (response.success) {
      return { ...response, data: response.data ?? { message: null } };
    }

    return { ...response, data: null };
  }

  async export(): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    return apiClient.downloadBlobWithFilename(
      API_CONFIG.ENDPOINTS.WORKING_DAYS.FILE,
      { method: 'GET' },
    );
  }

  async toggleStatus(
    id: number,
    isEnabled: boolean,
  ): Promise<ApiResponse<LC_ToggleWorkingDayStatusOut>> {
    const body: LC_ToggleWorkingDayStatusIn = { isEnabled };
    const response = await apiClient.patch<LC_ToggleWorkingDayStatusOut>(
      API_CONFIG.ENDPOINTS.WORKING_DAYS.TOGGLE_STATUS(id),
      body,
    );

    if (response.success) {
      return { ...response, data: response.data ?? { message: null } };
    }

    return { ...response, data: null };
  }
}

export const workingDayService = new WorkingDayService();
